import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import * as MUI from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import SimpleAlert from "../../../../../../components/display/SimpleAlert";
import { fetchWarehouseItemAuditing } from "../../../../../../enums/queryFunction/Fetch";
import { WAREHOUSE_ITEM_AUDITING } from "../../../../../../enums/QueryKeys";
import { CLINI_APPOINTMENT_DETAILS } from "../../../../../../enums/UrlPaths";
import { getMeasuringUnitLabel } from "../utils/measuringUnits";
import { formatCurrency } from "../utils/stockStatus";

const FIELD_LABELS = {
  title: "Title",
  description: "Description",
  category: "Category",
  supplier: "Supplier",
  price: "Price",
  pdv: "PDV",
  priceWithTax: "PriceWithVat",
  quantity: "Quantity",
  code: "Code",
  serialNumber: "SerialNumber",
  measuringUnit: "MeasuringUnit",
  discount: "Discount",
  antiRabiesVaccine: "AntiRabiesVaccine",
  otherVaccine: "OtherVaccine",
  narcoticDrug: "NarcoticDrug",
  prescriptionRequired: "PrescriptionRequired",
  expirationDate: "ExpirationDate",
};

const BOOLEAN_FIELDS = [
  "antiRabiesVaccine",
  "otherVaccine",
  "narcoticDrug",
  "prescriptionRequired",
];

const ACTION_COLORS = {
  Created: "success",
  Modified: "primary",
  Deleted: "error",
};

const ACTION_ICONS = {
  Created: <AddCircleOutlineIcon fontSize="small" />,
  Modified: <EditOutlinedIcon fontSize="small" />,
  Deleted: <DeleteOutlineIcon fontSize="small" />,
};

function formatFieldValue(field, value, t, currency) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (BOOLEAN_FIELDS.includes(field)) {
    return value ? t("Yes") : t("No");
  }
  switch (field) {
    case "category":
    case "supplier":
      return typeof value === "object" ? value.name : value;
    case "price":
    case "priceWithTax":
      return formatCurrency(value, currency);
    case "pdv":
    case "discount":
      return `${Number(value)}%`;
    case "expirationDate":
      return dayjs(value).format("DD/MM/YYYY");
    case "measuringUnit":
      return getMeasuringUnitLabel(value, t);
    default:
      return String(value);
  }
}

function ChangeRow({ field, change, t, currency }) {
  const label = FIELD_LABELS[field] || field;
  const oldValue = formatFieldValue(field, change?.oldValue, t, currency);
  const newValue = formatFieldValue(field, change?.newValue, t, currency);

  return (
    <MUI.Box>
      <MUI.Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
      >
        {t(label)}
      </MUI.Typography>
      <MUI.Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 0.5 }}
      >
        <MUI.Chip
          label={oldValue ?? t("Nothing")}
          size="small"
          variant="outlined"
          color="error"
          sx={{ textDecoration: oldValue !== null ? "line-through" : "none" }}
        />
        <MUI.Typography variant="body2" color="text.disabled">
          &rarr;
        </MUI.Typography>
        <MUI.Chip
          label={newValue ?? t("Nothing")}
          size="small"
          color="success"
          sx={{ fontWeight: 600 }}
        />
      </MUI.Stack>
    </MUI.Box>
  );
}

export default function WarehouseItemAuditingDialog({
  itemId,
  itemTitle,
  currency,
  open,
  onClose,
}) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const {
    data: revisions,
    isLoading,
    error,
  } = useQuery({
    queryKey: [WAREHOUSE_ITEM_AUDITING, itemId],
    queryFn: () => fetchWarehouseItemAuditing(itemId),
    enabled: Boolean(open && itemId),
    staleTime: 0,
  });

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.DialogTitle sx={{ p: { xs: 2.5, sm: 3.5 }, pr: 7 }}>
        <MUI.Stack direction="row" spacing={2} alignItems="center">
          <MUI.Avatar
            variant="rounded"
            sx={{
              bgcolor: "primary.main",
              width: 52,
              height: 52,
              borderRadius: 2.5,
            }}
          >
            <HistoryOutlinedIcon />
          </MUI.Avatar>
          <MUI.Box sx={{ minWidth: 0 }}>
            <MUI.Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.25 }}
              noWrap
            >
              {t("History")}
            </MUI.Typography>
            {itemTitle && (
              <MUI.Typography variant="caption" color="text.secondary" noWrap>
                {itemTitle}
              </MUI.Typography>
            )}
          </MUI.Box>
        </MUI.Stack>
        <MUI.IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 20, right: 20 }}
        >
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.DialogTitle>

      <MUI.DialogContent
        dividers
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          bgcolor: (theme) =>
            theme.palette.mode === "light"
              ? "grey.50"
              : "rgba(255,255,255,0.02)",
        }}
      >
        {isLoading && <MUI.LinearProgress />}

        {!isLoading && error && (
          <SimpleAlert
            severity="error"
            message="CouldntFetchWarehouseItemAuditErrorMessage"
          />
        )}

        {!isLoading && !error && (revisions ?? []).length === 0 && (
          <SimpleAlert severity="info" message="NoAuditHistoryFound" />
        )}

        {!isLoading && !error && (revisions ?? []).length > 0 && (
          <MUI.Stack spacing={2.5}>
            {revisions.map((revision) => {
              const actionColor = ACTION_COLORS[revision.action] ?? "primary";
              return (
                <MUI.Paper
                  key={revision.revisionId}
                  variant="outlined"
                  sx={{
                    p: { xs: 2, sm: 3 },
                    borderRadius: 3,
                    borderColor: "divider",
                    borderLeft: "4px solid",
                    borderLeftColor: `${actionColor}.main`,
                  }}
                >
                  <MUI.Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <MUI.Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <MUI.Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: (theme) =>
                            MUI.alpha(theme.palette[actionColor].main, 0.15),
                          color: `${actionColor}.main`,
                        }}
                      >
                        {ACTION_ICONS[revision.action] ?? (
                          <EditOutlinedIcon fontSize="small" />
                        )}
                      </MUI.Avatar>
                      <MUI.Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700 }}
                      >
                        {t(revision.action)}
                      </MUI.Typography>
                    </MUI.Stack>
                    <MUI.Typography variant="caption" color="text.secondary">
                      {dayjs(revision.timestamp).format("DD/MM/YYYY HH:mm")}
                    </MUI.Typography>
                  </MUI.Stack>

                  <MUI.Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {t("ModifiedBy")}:{" "}
                    {revision.modifiedBy?.fullName ?? t("NoInfo")}
                  </MUI.Typography>

                  {revision.changes &&
                    Object.keys(revision.changes).length > 0 && (
                      <MUI.Stack
                        spacing={1.5}
                        sx={{ mt: 2 }}
                        divider={<MUI.Divider />}
                      >
                        {Object.entries(revision.changes).map(
                          ([field, change]) => (
                            <ChangeRow
                              key={field}
                              field={field}
                              change={change}
                              t={t}
                              currency={currency}
                            />
                          ),
                        )}
                      </MUI.Stack>
                    )}

                  {revision?.usedInAppointments?.length > 0 && (
                    <Usage usageArr={revision.usedInAppointments} />
                  )}
                </MUI.Paper>
              );
            })}
          </MUI.Stack>
        )}
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}

function Usage({ usageArr = [] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  function handleNav(id) {
    if (!id) return;
    navigate(CLINI_APPOINTMENT_DETAILS + id);
  }

  return (
    <MUI.Box sx={{ mt: 2 }}>
      <MUI.Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
      >
        {t("Appointments")}
      </MUI.Typography>
      <MUI.Stack spacing={1} divider={<MUI.Divider />} sx={{ mt: 0.75 }}>
        {usageArr.map((item) => (
          <MUI.Box
            key={item?.appointmentId}
            role="button"
            tabIndex={0}
            onClick={() => handleNav(item?.appointmentId)}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              px: 1.25,
              py: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "primary.main",
              cursor: "pointer",
              transition: "background-color 0.15s ease",
              "&:hover": {
                bgcolor: (theme) =>
                  theme.palette.mode === "light"
                    ? "grey.100"
                    : "rgba(255,255,255,0.06)",
              },
            }}
          >
            <MUI.Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <MUI.Avatar
                sx={{
                  width: 28,
                  height: 28,
                  bgcolor: (theme) =>
                    MUI.alpha(theme.palette.primary.main, 0.15),
                  color: "primary.main",
                }}
              >
                <EventOutlinedIcon sx={{ fontSize: 16 }} />
              </MUI.Avatar>
              <MUI.Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {t("Appointment")} #{item?.appointmentId}
              </MUI.Typography>
            </MUI.Stack>

            <MUI.Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <MUI.Chip
                label={`${t("Used")}: ${item?.amountUsed}`}
                size="small"
                color="primary"
                sx={{ fontWeight: 600 }}
              />
              <ChevronRightIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
              />
            </MUI.Stack>
          </MUI.Box>
        ))}
      </MUI.Stack>
    </MUI.Box>
  );
}
